import { CheckCircle, XCircle } from '@timelyapp/icons/solid-icons';
import type { VariantProps } from 'class-variance-authority';
import { cva } from 'class-variance-authority';
import React from 'react';
import { Spinner } from '../spinner/Spinner';
import styles from './Circle.module.scss';

export type CircleLayout = 'list' | 'step' | 'wizard';
export type CircleState = 'ok' | 'error' | 'loading';

const circleStyles = cva(styles.circle, {
  variants: {
    layout: {
      list: styles['layout-list'],
      step: styles['layout-step'],
      wizard: styles['layout-wizard'],
    },
    variant: {
      solid: styles['variant-solid'],
      outline: styles['variant-outline'],
    },
  },
  defaultVariants: {
    layout: 'list',
    variant: 'solid',
  },
});

const indicatorStyles = cva(styles.indicator, {
  variants: {
    layout: {
      list: styles['indicator-list'],
      step: styles['indicator-step'],
      wizard: styles['indicator-wizard'],
    },
    status: {
      ok: styles['status-ok'],
      error: styles['status-error'],
      loading: styles['status-loading'],
    },
  },
  defaultVariants: {
    layout: 'list',
  },
});

const ICON_SIZE = {
  list: 10,
  step: 18,
  wizard: 14,
};

export interface CircleProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof circleStyles> {
  status?: CircleState;
  'data-testid'?: string;
}

// CircleIndicator is an internal component - not exported
const CircleIndicator = ({
  layout = 'list',
  status,
}: {
  layout?: CircleLayout;
  status: CircleState;
}) => {
  const iconSize = ICON_SIZE[layout];

  let icon: React.ReactNode = null;
  if (status === 'ok') {
    icon = <CheckCircle width={iconSize} height={iconSize} />;
  } else if (status === 'error') {
    icon = <XCircle width={iconSize} height={iconSize} />;
  } else if (status === 'loading') {
    icon = <Spinner size={iconSize - 1} />;
  }

  if (!icon) return null;

  return <div className={indicatorStyles({ layout, status })}>{icon}</div>;
};

export const Circle = React.forwardRef<HTMLDivElement, CircleProps>((props, ref) => {
  const {
    children,
    layout = 'list',
    variant = 'solid',
    status,
    className,
    'data-testid': dataTestId = 'circle',
    ...rest
  } = props;

  return (
    <div
      ref={ref}
      className={circleStyles({ layout, className, variant })}
      data-testid={dataTestId}
      {...rest}
    >
      {children}
      {status && <CircleIndicator layout={layout!} status={status} />}
    </div>
  );
});

Circle.displayName = 'Circle';
